import { useState, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { CircularProgress } from '@/components/ui/circular-progress';
import { FileAttachmentButton, FileAttachmentList } from '@/components/ui/file-attachment';
import { Square, ArrowUp } from 'lucide-react';
import { ModelSelector } from '@/components/ModelSelector';
import { cn } from '@/lib/utils';

/** Detect the likely file type from pasted text content */
function detectTextFileType(text: string): string {
  const trimmed = text.trim();

  // JSON detection
  if ((trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
    try {
      JSON.parse(trimmed);
      return 'json';
    } catch {
      // Not valid JSON, continue checking
    }
  }

  // TypeScript/TSX detection (check before JS since TS is more specific)
  if (trimmed.includes(': React.') ||
      trimmed.includes('interface ') ||
      trimmed.includes('type ') && trimmed.includes(' = {') ||
      /:\s*(string|number|boolean|void|any|unknown|never)\b/.test(trimmed) ||
      trimmed.includes('<T>') ||
      trimmed.includes('as const')) {
    // Check for JSX
    if (/<[A-Z][a-zA-Z]*[\s/>]/.test(trimmed) || trimmed.includes('className=')) {
      return 'tsx';
    }
    return 'ts';
  }

  // JavaScript/JSX detection
  if (trimmed.includes('import ') ||
      trimmed.includes('export ') ||
      trimmed.includes('const ') ||
      trimmed.includes('function ') ||
      trimmed.includes('=>') ||
      trimmed.includes('async ')) {
    // Check for JSX
    if (/<[A-Z][a-zA-Z]*[\s/>]/.test(trimmed) || trimmed.includes('className=')) {
      return 'jsx';
    }
    return 'js';
  }

  // HTML detection
  if (trimmed.startsWith('<!DOCTYPE') ||
      trimmed.startsWith('<html') ||
      (trimmed.startsWith('<') && /<\/[a-z]+>$/i.test(trimmed))) {
    return 'html';
  }

  // CSS detection
  if (/^[.#@]?[a-zA-Z][\w-]*\s*\{/.test(trimmed) ||
      trimmed.includes('@media') ||
      trimmed.includes('@keyframes')) {
    return 'css';
  }

  // Markdown detection
  if (trimmed.startsWith('# ') ||
      trimmed.startsWith('## ') ||
      /^\s*[-*]\s+/.test(trimmed) ||
      /\[.*\]\(.*\)/.test(trimmed)) {
    return 'md';
  }

  // YAML detection
  if (/^[a-zA-Z_]+:\s*.+$/m.test(trimmed) &&
      !trimmed.includes('{') &&
      !trimmed.includes(';')) {
    return 'yaml';
  }

  // Shell/Bash detection
  if (trimmed.startsWith('#!/bin/') ||
      trimmed.startsWith('$ ') ||
      /^(npm|yarn|pnpm|git|cd|ls|mkdir|rm|cp|mv|echo|cat)\s+/.test(trimmed)) {
    return 'sh';
  }

  // SQL detection
  if (/^(SELECT|INSERT|UPDATE|DELETE|CREATE|ALTER|DROP)\s+/i.test(trimmed)) {
    return 'sql';
  }

  // Python detection
  if (trimmed.includes('def ') ||
      trimmed.includes('import ') && trimmed.includes(':') ||
      trimmed.includes('class ') && trimmed.includes(':') ||
      /^\s*if\s+.*:$/m.test(trimmed)) {
    return 'py';
  }

  // Default to plain text
  return 'txt';
}

export type ChatMode = 'ask' | 'act';

interface ChatInputProps {
  isLoading: boolean;
  isConfigured: boolean;
  providerModel: string;
  onProviderModelChange: (model: string) => void;
  onSend: (input: string, files: File[]) => void;
  onStop: () => void;
  onFocus: () => void;
  onFirstInteraction: () => void;
  isModelSelectorOpen: boolean;
  onModelSelectorOpenChange: (open: boolean) => void;
  contextUsagePercentage: number;
  currentModelContextLength?: number;
  lastInputTokens?: number;
  totalCost: number;
  isDragOver: boolean;
  onDragEnter: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  chatMode: ChatMode;
  onChatModeChange: (mode: ChatMode) => void;
}

export const ChatInput = memo(function ChatInput({
  isLoading,
  isConfigured,
  providerModel,
  onProviderModelChange,
  onSend,
  onStop,
  onFocus,
  onFirstInteraction,
  isModelSelectorOpen,
  onModelSelectorOpenChange,
  contextUsagePercentage,
  currentModelContextLength,
  lastInputTokens,
  totalCost,
  isDragOver,
  onDragEnter,
  onDragOver,
  onDragLeave,
  onDrop,
  chatMode,
  onChatModeChange,
}: ChatInputProps) {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);

  const handleFileSelect = useCallback((file: File) => {
    setAttachedFiles(prev => [...prev, file]);
  }, []);

  const handleFileRemove = useCallback((fileToRemove: File) => {
    setAttachedFiles(prev => prev.filter(file => file !== fileToRemove));
  }, []);

  const handleSend = useCallback(() => {
    if ((!input.trim() && attachedFiles.length === 0) || isLoading) return;
    if (!isConfigured || !providerModel.trim()) return;

    const currentInput = input;
    const currentFiles = [...attachedFiles];

    setInput('');
    setAttachedFiles([]);

    onSend(currentInput, currentFiles);
  }, [input, attachedFiles, isLoading, isConfigured, providerModel, onSend]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  const handlePaste = useCallback(async (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    // Check for images first
    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      // Check if the item is an image
      if (item.type.startsWith('image/')) {
        e.preventDefault(); // Prevent default paste behavior for images

        const file = item.getAsFile();
        if (file) {
          // Generate a filename with timestamp
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
          const extension = file.type.split('/')[1] || 'png';
          const filename = `pasted-image-${timestamp}.${extension}`;

          // Create a new File object with the generated name
          const namedFile = new File([file], filename, { type: file.type });

          // Add to attached files
          setAttachedFiles(prev => [...prev, namedFile]);
        }
        return; // Exit after handling image
      }
    }

    // Check for large text paste
    const pastedText = e.clipboardData?.getData('text');
    if (pastedText) {
      const lineCount = pastedText.split('\n').length;
      const charCount = pastedText.length;

      // Threshold: more than 500 chars OR more than 15 lines
      if (charCount > 500 || lineCount > 15) {
        e.preventDefault(); // Prevent default paste behavior

        // Try to detect file type from content
        const extension = detectTextFileType(pastedText);
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const filename = `pasted-text-${timestamp}.${extension}`;

        // Create a text file from the pasted content
        const textFile = new File([pastedText], filename, { type: 'text/plain' });

        // Add to attached files
        setAttachedFiles(prev => [...prev, textFile]);
      }
    }
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
  }, []);

  return (
    <div className="border-t border-border p-4">
      {/* Chat Input Container */}
      <div
        className={`flex flex-col rounded-2xl border-2 border-border bg-background shadow-sm focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:border-primary/50 transition-all ${
          isDragOver ? 'border-primary bg-primary/5 ring-2 ring-primary/20' : ''
        }`}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {/* Attachments Row - displayed above textarea */}
        {attachedFiles.length > 0 && (
          <div className="px-4 pt-3 pb-1">
            <FileAttachmentList
              files={attachedFiles}
              onFileRemove={handleFileRemove}
            />
          </div>
        )}

        <Textarea
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onFocus={onFocus}
          placeholder={
            !isConfigured
              ? t('askToAddFeatures')
              : providerModel.trim()
                ? t('askToAddFeatures')
                : t('selectModelFirst')
          }
          className="flex-1 resize-none border-0 bg-transparent px-4 py-3 text-sm focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground"
          disabled={isLoading || (isConfigured && !providerModel.trim())}
          rows={1}
          aria-label="Chat message input"
          style={{
            height: 'auto',
            minHeight: '96px'
          }}
          onInput={(e) => {
            const target = e.target as HTMLTextAreaElement;
            target.style.height = 'auto';
            target.style.height = Math.min(target.scrollHeight, 128) + 'px';
          }}
        />

        {/* Bottom Controls Row */}
        <div className="flex items-center gap-4 px-2 py-2">
          {/* Ask/Act Mode Toggle */}
          <div className="flex items-center rounded-full bg-muted p-0.5 text-xs">
            <button
              type="button"
              onClick={() => onChatModeChange('ask')}
              className={cn(
                'px-2.5 py-1 rounded-full transition-all font-medium',
                chatMode === 'ask'
                  ? 'bg-background text-blue-600 dark:text-blue-400 shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {t('ask')}
            </button>
            <button
              type="button"
              onClick={() => onChatModeChange('act')}
              className={cn(
                'px-2.5 py-1 rounded-full transition-all font-medium',
                chatMode === 'act'
                  ? 'bg-background text-orange-600 dark:text-orange-400 shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {t('act')}
            </button>
          </div>

          {/* File Attachment Button */}
          <FileAttachmentButton
            onFileSelect={handleFileSelect}
            disabled={isLoading}
            multiple={true}
          />

          {/* Context Usage Wheel */}
          {contextUsagePercentage >= 10 && currentModelContextLength && lastInputTokens && lastInputTokens > 0 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="cursor-help">
                    <CircularProgress
                      value={contextUsagePercentage}
                      size={20}
                      strokeWidth={2}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t('contextUsage', {
                    tokens: lastInputTokens.toLocaleString(),
                    total: currentModelContextLength.toLocaleString(),
                    percentage: contextUsagePercentage.toFixed(1)
                  })}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {/* Cost Display */}
          {totalCost >= 0.01 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="text-xs text-muted-foreground px-2 py-1 bg-muted/50 rounded-md whitespace-nowrap cursor-help">
                    ${totalCost.toFixed(2)}
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t('totalCostSession')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {/* Model Selector */}
          <div className="flex-1 max-w-72 ml-auto overflow-hidden">
            <ModelSelector
              value={providerModel}
              onChange={onProviderModelChange}
              disabled={isLoading}
              placeholder={t('chooseModel')}
              open={isModelSelectorOpen}
              onOpenChange={onModelSelectorOpenChange}
            />
          </div>

          {/* Send/Stop Button */}
          <div>
            {isLoading ? (
              <Button
                onClick={onStop}
                size="sm"
                variant="ghost"
                className="size-8 rounded-full p-0 bg-foreground/10 [&_svg]:size-3.5 [&_svg]:fill-foreground hover:bg-foreground/20"
              >
                <Square />
              </Button>
            ) : (
              <Button
                onClick={handleSend}
                onMouseDown={onFirstInteraction}
                disabled={(!input.trim() && attachedFiles.length === 0) || (isConfigured && !providerModel.trim())}
                size="sm"
                className="size-8 [&_svg]:size-5 rounded-full p-0"
              >
                <ArrowUp />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});
